import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ButtonComp,
  CardComp,
  ModalCompAdvanceUpdate,
  TabsComp,
} from "../../../components";
import { ToolTips } from "../../../components/Ui/ToolTips";
import { formatMoney, convertToTitleCase } from "../../../utils";
import { BsInfoCircle } from "react-icons/bs";
import {
  MemberContributionsHistory,
  MemberWithdrawalRequestsHeader,
} from "../../../utils/TableHeader";
import PageWrapper from "../modules/PageWrapper";
import MemberContributionSummary from "./MemberContributionSummary";
import SearchBar from "../modules/SearchBar";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import ConfirmationModal from "../../../components/Common/ConfirmationModal";
import {
  TableCompData,
  TextInputComp,
  MoneyInputComp,
} from "../../../components";
import EmptyTable from "../../../components/Common/EmptyTable";
import TablePagination from "../../../components/Table/Pagination";
import {
  useGetMemberContributionSummaryQuery,
  useGetMemberContributionHistoryQuery,
} from "../../../store/member/member-contribution";
import {
  useCreateWithdrawalRequestMutation,
  useGetMyWithdrawalRequestsQuery,
  useCancelWithdrawalRequestMutation,
} from "../../../store/member/member-savings-withdrawal";
import { useGetMyBankAccountQuery } from "../../../store/member/member-bank-account";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import moment from "moment";

/**
 * payment_date is often date-only (YYYY-MM-DD), which parses as local midnight — wrong for "time".
 * created_at is the actual payment row timestamp; use it for clock time when present.
 */
function contributionMomentForCalendarDate(contribution) {
  return moment(contribution?.payment_date || contribution?.created_at);
}

function contributionMomentForClockTime(contribution) {
  return moment(contribution?.created_at || contribution?.payment_date);
}

export default function MemberContribution() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedContribution, setSelectedContribution] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [modalPage, setModalPage] = useState("");
  const [filterType, setFilterType] = useState("all"); // "all", "withdrawable", "non-withdrawable"
  const [categoryFilter, setCategoryFilter] = useState(""); // chosen from "Where my money went"

  // Fetch contribution summary (totals only) - optimized for dashboard display
  // This endpoint returns just the summary values without fetching full history
  // Note: member_id is not needed - backend gets it from authenticated token
  const {
    data: contributionSummaryData,
    isFetching: isSummaryFetching,
    isLoading: isSummaryLoading,
  } = useGetMemberContributionSummaryQuery();

  // Fetch contribution history with backend filtering
  // Filtering and search are now handled by the backend
  // Note: member_id is not needed - backend gets it from authenticated token
  const {
    data: contributionHistoryData,
    isFetching,
    isLoading,
  } = useGetMemberContributionHistoryQuery({
    page: page,
    limit: 20,
    search: search || undefined, // Send search to backend (only if not empty)
    filter_type: filterType, // Always send filter_type (backend handles "all")
    contribution_category_id: categoryFilter || undefined,
  });

  const contributionHistory = contributionHistoryData?.contributions || [];
  const pagination = contributionHistoryData?.pagination || {};

  // Use summary data from API endpoint (optimized for mobile)
  // total_savings | total_shares | total_contribution (= grand total)
  const totals = React.useMemo(() => {
    if (contributionSummaryData) {
      return {
        total: parseFloat(
          contributionSummaryData.total_contribution ||
            contributionSummaryData.total_contributed ||
            0
        ),
        savings: parseFloat(contributionSummaryData.total_savings || 0),
        shares: parseFloat(contributionSummaryData.total_shares || 0),
      };
    }

    return {
      total: 0,
      savings: 0,
      shares: 0,
    };
  }, [contributionSummaryData]);

  // Format table data for a given array of contributions
  const formatTableData = (contributions) => {
    return contributions.map((contribution, index) => ({
      id: index + 1,
      name:
        contribution.contribution_category?.name ||
        contribution.category?.name ||
        "N/A",
      amount: contribution.amount || 0,
      paymentType: convertToTitleCase(
        contribution.payment_method?.replace("_", " ") || "Paystack"
      ),
      date: contributionMomentForCalendarDate(contribution).format(
        "DD-MMM-YYYY"
      ),
      time: contributionMomentForClockTime(contribution).format("hh:mm A"),
      status: contribution.status || "SUCCESSFUL",
      originalData: contribution,
    }));
  };

  // Use contributions directly from backend (already filtered)
  const tableData = formatTableData(contributionHistory);

  const TableDropDown = [
    {
      name: "View Details",
      action: (data) => {
        setSelectedContribution(data?.originalData);
        setModalPage("ViewDetails");
      },
    },
  ];

  return (
    <>
      <PageWrapper title={"Contributions"}>
        <MemberContributionSummary
          totals={totals}
          loading={isSummaryLoading || isSummaryFetching}
          onWithdraw={() => setModalPage("WithdrawSavings")}
          activeCategory={categoryFilter}
          onPickCategory={(id) => {
            setCategoryFilter(id);
            setPage(1);
            document.querySelector('[data-tour="member-contrib-history"]')?.scrollIntoView?.({ behavior: "smooth", block: "start" });
          }}
        />
        <section data-tour="member-contrib-history">
          {/* Tabs for Contribution History and Withdrawal Requests */}
          <CardComp cardClassName={"border-0 pt-3 px-3 mb-4"}>
            <TabsComp
              TabArray={[
                {
                  name: "Contribution History",
                  component: (
                    <ContributionHistoryTab
                      filterType={filterType}
                      setFilterType={setFilterType}
                      search={search}
                      setSearch={setSearch}
                      page={page}
                      setPage={setPage}
                      totals={totals}
                      tableData={tableData}
                      isFetching={isFetching}
                      isLoading={isLoading}
                      pagination={pagination}
                      TableDropDown={TableDropDown}
                    />
                  ),
                },
                {
                  name: "Withdrawal Requests",
                  component: (
                    <WithdrawalRequestsTab
                      setModalPage={setModalPage}
                      setSelectedRequest={setSelectedRequest}
                    />
                  ),
                },
              ]}
              tabClassName="col-6"
              lineHeight={"2px"}
              borderWidth="3px"
              lineBottom={"24px"}
              input="name"
            />
          </CardComp>
        </section>
      </PageWrapper>

      {/* Contribution Details Modal */}
      <ModalCompAdvanceUpdate
        size="md"
        pageName={modalPage}
        centered
        arrayComp={[
          {
            name: "ViewDetails",
            component: selectedContribution && (
              <ContributionDetailsModal
                contribution={selectedContribution}
                handleClose={() => {
                  setModalPage("");
                  setSelectedContribution(null);
                }}
              />
            ),
          },
          {
            name: "WithdrawSavings",
            component: (
              <WithdrawSavingsModal
                withdrawableAmount={totals.savings}
                handleClose={() => {
                  setModalPage("");
                }}
              />
            ),
          },
          {
            name: "ViewWithdrawalDetails",
            component: selectedRequest && (
              <WithdrawalDetailsModal
                request={selectedRequest}
                handleClose={() => {
                  setModalPage("");
                  setSelectedRequest(null);
                }}
              />
            ),
          },
        ]}
        handleClose={() => {
          setModalPage("");
          setSelectedContribution(null);
          setSelectedRequest(null);
        }}
        close
      />
    </>
  );
}

// Contribution History Tab Component
const ContributionHistoryTab = ({
  filterType,
  setFilterType,
  search,
  setSearch,
  page,
  setPage,
  totals,
  tableData,
  isFetching,
  isLoading,
  pagination,
  TableDropDown,
}) => {
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-2">
            <h4 className="fw-bold text-black1 mb-0">Contribution History</h4>
            <ToolTips
              showText="Withdrawable: Your savings in different categories that can be withdrawn. Non-Withdrawable: Your contributions to the organization that cannot be withdrawn."
              text={
                <BsInfoCircle
                  size={18}
                  style={{ color: "var(--color-secondary)", cursor: "help" }}
                />
              }
            />
          </div>
          <p className="text-muted small mb-0">
            {filterType === "all" && (
              <>
                Total: <strong>{formatMoney(totals.total, true, "₦")}</strong>
              </>
            )}
            {filterType === "withdrawable" && (
              <>
                Savings (withdrawable):{" "}
                <strong>{formatMoney(totals.savings, true, "₦")}</strong>
              </>
            )}
            {filterType === "non-withdrawable" && (
              <>
                Shares:{" "}
                <strong>{formatMoney(totals.shares, true, "₦")}</strong>
                {" "}(filter includes shares and other non-savings)
              </>
            )}
          </p>
        </div>
        <div className="d-flex gap-4 align-items-center flex-wrap">
          <div style={{ minWidth: "220px", flex: "0 0 auto" }}>
            <select
              className="form-select py-2 px-3"
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setPage(1);
              }}
              style={{
                width: "100%",
                backgroundColor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: "5px",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              <option value="all">All Contributions</option>
              <option value="withdrawable">Withdrawable Only</option>
              <option value="non-withdrawable">Non-Withdrawable Only</option>
            </select>
          </div>
          <div style={{ flex: "1 1 300px", minWidth: "250px" }}>
            <SearchBar
              placeholder={"Search contributions..."}
              value={search}
              onSearch={(value) => {
                setSearch(value);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>
      <div>
        <TableCompData
          columns={MemberContributionsHistory(TableDropDown)}
          data={tableData}
          marginBottom={"2rem"}
          progressPending={isFetching || isLoading}
          progressComponent={<TableSkeletonLoader rows={10} columns={7} />}
          message={
            !isFetching && !isLoading && tableData.length === 0 ? (
              <EmptyTable
                Message={
                  filterType === "all"
                    ? "No contributions found. Record your first contribution to get started."
                    : filterType === "withdrawable"
                    ? "No withdrawable contributions found."
                    : "No non-withdrawable contributions found."
                }
              />
            ) : null
          }
        />
      </div>
      {/* Pagination */}
      {pagination && pagination.total > 0 && (
        <div className="mt-4">
          <TablePagination
            marginTop={"1rem"}
            handlePageChange={setPage}
            currentPage={pagination.currentPage || page}
            totalPages={pagination.totalPages || 1}
            count={pagination.count || tableData.length || 0}
            total={pagination.total || 0}
            perPage={pagination.perPage || 20}
          />
        </div>
      )}
    </div>
  );
};

// Withdrawal Requests Tab Component
const WithdrawalRequestsTab = ({ setModalPage, setSelectedRequest }) => {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [requestToCancel, setRequestToCancel] = useState(null);

  const {
    data: withdrawalRequestsData,
    isFetching,
    isLoading,
    refetch,
  } = useGetMyWithdrawalRequestsQuery({
    page,
    limit: 20,
    status: statusFilter,
  });

  const [
    cancelRequest,
    { isLoading: isCanceling },
  ] = useCancelWithdrawalRequestMutation();

  const withdrawalRequests = withdrawalRequestsData?.withdrawalRequests || [];
  const pagination = withdrawalRequestsData?.pagination || {};

  // Format table data
  const formatTableData = (requests) => {
    return requests.map((request, index) => ({
      id: request.id,
      serialNumber: (page - 1) * 20 + index + 1,
      amount: request.amount || 0,
      status: request.status || "PENDING",
      requestDate: request.created_at || request.request_date,
      bank_account: request.bank_account || null,
      bank_name:
        request.bank_account?.bank_name ||
        request.bank_account?.bank?.name ||
        request.bank_name ||
        null,
      account_number:
        request.bank_account?.account_number || request.account_number || null,
      description: request.description || "",
      decline_reason: request.decline_reason || "",
      originalData: request,
    }));
  };

  const tableData = formatTableData(withdrawalRequests);

  // Handle cancel confirmation
  const handleCancelConfirm = async () => {
    if (!requestToCancel) return;

    try {
      const response = await cancelRequest(requestToCancel.id);
      if (response.error) {
        toast.error(
          response.error?.data?.message || "Failed to cancel request"
        );
      } else if (response.data?.status === false) {
        toast.error(response.data?.message || "Failed to cancel request");
      } else {
        toast.success(response.data?.message || "Withdrawal request canceled");
        refetch();
      }
    } catch (error) {
      toast.error("An error occurred while canceling the request");
    } finally {
      setShowCancelConfirm(false);
      setRequestToCancel(null);
    }
  };

  // Table dropdown actions - function that takes row and returns actions array
  const getTableDropDown = (row) => {
    const actions = [];
    const status = (row?.status || "").toUpperCase();

    // View Details - always available
    actions.push({
      name: "View Details",
      action: () => {
        setSelectedRequest(row?.originalData);
        setModalPage("ViewWithdrawalDetails");
      },
    });

    // Cancel - only for pending requests
    if (status === "PENDING") {
      actions.push({
        name: "Cancel Request",
        action: () => {
          setRequestToCancel(row);
          setShowCancelConfirm(true);
        },
      });
    }

    return actions;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
        <div>
          <h4 className="fw-bold text-black1 mb-0">Withdrawal Requests</h4>
          <p className="text-muted small mb-0">
            View and manage your savings withdrawal requests
          </p>
        </div>
        <div style={{ minWidth: "220px", flex: "0 0 auto" }}>
          <select
            className="form-select py-2 px-3"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            style={{
              width: "100%",
              backgroundColor: "#fafafa",
              border: "1px solid #e0e0e0",
              borderRadius: "5px",
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            <option value="all">All Requests</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="declined">Declined</option>
            <option value="paid">Paid</option>
            <option value="canceled">Canceled</option>
          </select>
        </div>
      </div>
      <div>
        <TableCompData
          columns={MemberWithdrawalRequestsHeader(getTableDropDown)}
          data={tableData}
          marginBottom={"2rem"}
          progressPending={isFetching || isLoading}
          progressComponent={<TableSkeletonLoader rows={10} columns={6} />}
          message={
            !isFetching && !isLoading && tableData.length === 0 ? (
              <EmptyTable
                Message={
                  statusFilter === "all"
                    ? "No withdrawal requests found. Click 'Withdraw Savings' to create a new request."
                    : `No ${statusFilter} withdrawal requests found.`
                }
              />
            ) : null
          }
        />
      </div>
      {/* Pagination */}
      {pagination && pagination.total > 0 && (
        <div className="mt-4">
          <TablePagination
            marginTop={"1rem"}
            handlePageChange={setPage}
            currentPage={pagination.currentPage || page}
            totalPages={pagination.totalPages || 1}
            count={pagination.count || tableData.length || 0}
            total={pagination.total || 0}
            perPage={pagination.perPage || 20}
          />
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      <ConfirmationModal
        isOpen={showCancelConfirm}
        onClose={() => {
          setShowCancelConfirm(false);
          setRequestToCancel(null);
        }}
        onConfirm={handleCancelConfirm}
        title="Cancel Withdrawal Request"
        message="Are you sure you want to cancel this withdrawal request? The amount will be restored to your savings."
        confirmText="Yes, Cancel Request"
        cancelText="No, Keep Request"
        confirmButtonClass="bg-primary1 text-white"
        cancelButtonClass="bg-secondary text-white"
        isLoading={isCanceling}
      />
    </div>
  );
};

// Withdraw Savings Modal Component
const WithdrawSavingsModal = ({ withdrawableAmount, handleClose }) => {
  const navigate = useNavigate();
  const {
    data: bankAccount,
    isLoading: isLoadingBank,
  } = useGetMyBankAccountQuery();
  const [
    createWithdrawal,
    { isLoading: isSubmitting },
  ] = useCreateWithdrawalRequestMutation();

  // Check if member has a valid bank account
  const hasValidBankAccount = (account) => {
    if (!account) return false;
    if (account === null) return false;
    if (typeof account !== "object") return false;
    if (Object.keys(account).length === 0) return false;
    // Check for various possible field names that indicate a valid bank account
    return !!(
      account.id ||
      account.account_number ||
      account.bank?.name ||
      account.bank_name ||
      (account.bank && account.account_number)
    );
  };

  const bankAccountExists = hasValidBankAccount(bankAccount);

  const validationSchema = Yup.object({
    amount: Yup.number()
      .required("Amount is required")
      .min(1, "Amount must be greater than 0")
      .max(
        withdrawableAmount,
        `Amount cannot exceed your available savings of ${formatMoney(
          withdrawableAmount,
          true,
          "₦"
        )}`
      )
      .test(
        "max-decimal",
        "Amount cannot have more than 2 decimal places",
        (value) => {
          if (!value) return true;
          const decimalPlaces = (value.toString().split(".")[1] || "").length;
          return decimalPlaces <= 2;
        }
      ),
    description: Yup.string().max(
      500,
      "Description must be less than 500 characters"
    ),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    // Double-check bank account before submission (defensive check)
    // Since the form is only shown when bankAccountExists is true, this should rarely trigger
    // But we check the actual bankAccount object to be safe
    const hasAccount =
      bankAccount &&
      (bankAccount.id ||
        bankAccount.account_number ||
        (bankAccount.bank && bankAccount.bank.name) ||
        bankAccount.bank_name);

    if (!hasAccount) {
      toast.error(
        "Please add a bank account from your profile before requesting a withdrawal."
      );
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
        amount: parseFloat(values.amount),
        description: values.description || undefined,
      };

      const response = await createWithdrawal(payload);
      if (response.error) {
        toast.error(
          response.error?.data?.message || "Failed to create withdrawal request"
        );
      } else if (response.data?.status === false) {
        toast.error(
          response.data?.message || "Failed to create withdrawal request"
        );
      } else {
        toast.success(
          response.data?.message || "Withdrawal request submitted successfully"
        );
        handleClose();
        // Refetch will be handled by cache invalidation
      }
    } catch (error) {
      toast.error("An error occurred while submitting the request");
      console.error("Withdrawal request error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="w-full px-3 px-lg-4"
      style={{ paddingTop: "20px", paddingBottom: "20px" }}
    >
      <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
        <div>
          <h5 className="fw-bold text-black1 mb-1" style={{ fontSize: "20px" }}>
            Withdraw Savings
          </h5>
          <p className="text-muted small mb-0">
            Available:{" "}
            <strong className="text-black1">
              {formatMoney(withdrawableAmount, true, "₦")}
            </strong>
          </p>
        </div>
        <img
          src="/Icon/cancel.svg"
          role="button"
          onClick={handleClose}
          alt="close"
          className="close-img h-5"
          style={{ cursor: "pointer" }}
        />
      </div>

      {isLoadingBank ? (
        <div className="text-center py-4">
          <p className="text-muted">Loading bank account information...</p>
        </div>
      ) : !bankAccountExists ? (
        <div className="py-4">
          <div
            className="alert alert-warning d-flex align-items-center gap-3 mb-4"
            role="alert"
            style={{
              backgroundColor: "#fce2fa",
              border: "2px solid #ed49e0",
              borderRadius: "10px",
              padding: "20px",
            }}
          >
            <BsInfoCircle size={24} style={{ color: "#ed49e0" }} />
            <div>
              <h6 className="fw-bold mb-2" style={{ color: "var(--color-primary)" }}>
                Bank Account Required
              </h6>
              <p className="mb-2" style={{ fontSize: "14px" }}>
                You need to add a bank account to your profile before you can
                request a withdrawal.
              </p>
              <ButtonComp
                btnText={"Go to Profile"}
                onClick={() => {
                  handleClose();
                  navigate("/member/profile?tab=bank_account");
                }}
                btnClassName="px-4 py-2 bg-primary1 text-white rounded"
                BorderColor={"transparent"}
              />
            </div>
          </div>
        </div>
      ) : (
        <Formik
          initialValues={{
            amount: "",
            description: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting: formikIsSubmitting, isValid }) => (
            <Form>
              <div className="d-flex flex-column gap-4">
                {/* Bank Account Info */}
                {bankAccount && (
                  <div
                    className="p-3 rounded"
                    style={{
                      backgroundColor: "#eaf1f4",
                      border: "1px solid var(--color-secondary)",
                    }}
                  >
                    <p className="text-muted small mb-1">Bank Account</p>
                    <p className="fw-bold text-black1 mb-0">
                      {bankAccount?.bank?.name ||
                        bankAccount?.bank_name ||
                        "N/A"}
                    </p>
                    <p className="text-muted small mb-0">
                      {bankAccount?.account_number || "N/A"}
                    </p>
                    <p className="text-muted small mb-0">
                      {bankAccount?.account_name || "N/A"}
                    </p>
                  </div>
                )}

                {/* Amount Input */}
                <div>
                  <MoneyInputComp
                    LabelText={"Withdrawal Amount"}
                    LabelClassName={"fw-bold"}
                    placeholder={"Enter amount"}
                    name="amount"
                    defaultValue=""
                  />
                  <p className="text-muted small mt-1">
                    Maximum: {formatMoney(withdrawableAmount, true, "₦")}
                  </p>
                </div>

                {/* Description Input (Optional) */}
                <div>
                  <TextInputComp
                    LabelText={"Description (Optional)"}
                    LabelClassName={"fw-bold"}
                    placeholder={"Add a note about this withdrawal"}
                    name="description"
                    type="text"
                  />
                </div>

                {/* Submit Button */}
                <div className="d-flex gap-3 justify-content-center pt-2">
                  <ButtonComp
                    btnText={"Cancel"}
                    onClick={handleClose}
                    btnClassName="px-4 py-2 rounded"
                    style={{
                      backgroundColor: "transparent",
                      border: "2px solid var(--color-primary)",
                      color: "var(--color-primary)",
                    }}
                    type="button"
                  />
                  <ButtonComp
                    btnText={
                      isSubmitting || formikIsSubmitting
                        ? "Submitting..."
                        : "Submit Request"
                    }
                    btnClassName="px-4 py-2 bg-primary1 text-white rounded"
                    BorderColor={"transparent"}
                    type="submit"
                    disabled={isSubmitting || formikIsSubmitting || !isValid}
                  />
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};

// Withdrawal Details Modal Component
const WithdrawalDetailsModal = ({ request, handleClose }) => {
  const status = (request?.status || "").toUpperCase();
  const getStatusColor = (status) => {
    if (status === "PAID" || status === "APPROVED") {
      return "#19BC31"; // Green
    }
    if (status === "PENDING") {
      return "#FFA500"; // Orange
    }
    if (status === "DECLINED" || status === "CANCELED") {
      return "#FF4343"; // Red
    }
    return "#A1A0A0"; // Grey
  };

  return (
    <div className="w-full py-5 px-3 px-lg-4">
      <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
        <h5 className="fw-bold text-black1 mb-0" style={{ fontSize: "20px" }}>
          Withdrawal Request Details
        </h5>
        <img
          src="/Icon/cancel.svg"
          role="button"
          onClick={handleClose}
          alt="close"
          className="close-img h-5"
          style={{ cursor: "pointer" }}
        />
      </div>

      <div className="w-full d-flex flex-column gap-3">
        <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
          <p className="text-sm text-muted mb-0">Request Date</p>
          <p className="text-sm fw-bold text-black1 mb-0">
            {moment(request?.created_at || request?.request_date).format(
              "DD-MMM-YYYY"
            )}
          </p>
        </div>

        <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
          <p className="text-sm text-muted mb-0">Request Time</p>
          <p className="text-sm fw-bold text-black1 mb-0">
            {moment(request?.created_at || request?.request_date).format(
              "hh:mm A"
            )}
          </p>
        </div>

        <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
          <p className="text-sm text-muted mb-0">Amount</p>
          <p className="text-sm fw-bold text-black1 mb-0">
            {formatMoney(request?.amount || 0, true, "₦")}
          </p>
        </div>

        <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
          <p className="text-sm text-muted mb-0">Status</p>
          <span
            className="px-3 py-1 rounded-pill text-white fw-semibold"
            style={{
              backgroundColor: getStatusColor(status),
              fontSize: "12px",
            }}
          >
            {convertToTitleCase(status)}
          </span>
        </div>

        {request?.bank_account && (
          <>
            <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
              <p className="text-sm text-muted mb-0">Bank Name</p>
              <p className="text-sm fw-bold text-black1 mb-0">
                {request.bank_account?.bank_name ||
                  request.bank_account?.bank?.name ||
                  request.bank_name ||
                  "N/A"}
              </p>
            </div>

            <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
              <p className="text-sm text-muted mb-0">Account Number</p>
              <p className="text-sm fw-bold text-black1 mb-0 font-monospace">
                {request.bank_account?.account_number ||
                  request.account_number ||
                  "N/A"}
              </p>
            </div>

            <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
              <p className="text-sm text-muted mb-0">Account Name</p>
              <p className="text-sm fw-bold text-black1 mb-0">
                {request.bank_account?.account_name ||
                  request.account_name ||
                  "N/A"}
              </p>
            </div>
          </>
        )}

        {request?.description && (
          <div className="w-100 pb-3 border-bottom d-flex flex-column">
            <p className="text-sm text-muted mb-2">Description</p>
            <p className="text-sm fw-bold text-black1 mb-0">
              {request.description}
            </p>
          </div>
        )}

        {request?.decline_reason && (
          <div className="w-100 pb-3 border-bottom d-flex flex-column">
            <p className="text-sm text-muted mb-2">Decline Reason</p>
            <p className="text-sm fw-bold text-danger mb-0">
              {request.decline_reason}
            </p>
          </div>
        )}

        {request?.approved_by && (
          <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
            <p className="text-sm text-muted mb-0">Approved By</p>
            <p className="text-sm fw-bold text-black1 mb-0">
              {request.approved_by?.name || request.approved_by_name || "N/A"}
            </p>
          </div>
        )}

        {request?.declined_by && (
          <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
            <p className="text-sm text-muted mb-0">Declined By</p>
            <p className="text-sm fw-bold text-black1 mb-0">
              {request.declined_by?.name || request.declined_by_name || "N/A"}
            </p>
          </div>
        )}

        {request?.paid_by && (
          <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
            <p className="text-sm text-muted mb-0">Paid By</p>
            <p className="text-sm fw-bold text-black1 mb-0">
              {request.paid_by?.name || request.paid_by_name || "N/A"}
            </p>
          </div>
        )}

        {request?.payment_date && (
          <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
            <p className="text-sm text-muted mb-0">Payment Date</p>
            <p className="text-sm fw-bold text-black1 mb-0">
              {moment(request.payment_date).format("DD-MMM-YYYY")}
            </p>
          </div>
        )}

        {request?.payment_reference && (
          <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
            <p className="text-sm text-muted mb-0">Payment Reference</p>
            <p className="text-sm fw-bold text-black1 mb-0 font-monospace">
              {request.payment_reference}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Contribution Details Modal Component
const ContributionDetailsModal = ({ contribution, handleClose }) => {
  const category =
    contribution?.contribution_category || contribution?.category;
  const isWithdrawable = category?.is_withdrawable === 1;

  return (
    <div className="w-full py-5 px-3 px-lg-4">
      <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
        <h5 className="fw-bold text-black1 mb-0" style={{ fontSize: "20px" }}>
          Contribution Details
        </h5>
        <img
          src="/Icon/cancel.svg"
          role="button"
          onClick={handleClose}
          alt="close"
          className="close-img h-5"
          style={{ cursor: "pointer" }}
        />
      </div>

      <div className="w-full d-flex flex-column gap-3">
        <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
          <p className="text-sm text-muted mb-0">Contribution Name</p>
          <p className="text-sm fw-bold text-black1 mb-0">
            {category?.name || "N/A"}
          </p>
        </div>

        <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
          <p className="text-sm text-muted mb-0">Amount</p>
          <p className="text-sm fw-bold text-black1 mb-0">
            {formatMoney(contribution?.amount || 0, true, "₦")}
          </p>
        </div>

        <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
          <p className="text-sm text-muted mb-0">Payment Method</p>
          <p className="text-sm fw-bold text-black1 mb-0">
            {convertToTitleCase(
              contribution?.payment_method?.replace("_", " ") || "Paystack"
            )}
          </p>
        </div>

        <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
          <p className="text-sm text-muted mb-0">Payment Date</p>
          <p className="text-sm fw-bold text-black1 mb-0">
            {contributionMomentForCalendarDate(contribution).format(
              "DD-MMM-YYYY"
            )}
          </p>
        </div>

        <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
          <p className="text-sm text-muted mb-0">Payment Time</p>
          <p className="text-sm fw-bold text-black1 mb-0">
            {contributionMomentForClockTime(contribution).format("hh:mm A")}
          </p>
        </div>

        <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
          <p className="text-sm text-muted mb-0">Status</p>
          <span
            className="px-3 py-1 rounded-pill text-white fw-semibold"
            style={{
              backgroundColor:
                contribution?.status === "SUCCESSFUL" ||
                contribution?.status === "PROCESSED"
                  ? "#19BC31"
                  : contribution?.status === "PENDING"
                  ? "#FFA500"
                  : "#FF4343",
              fontSize: "12px",
            }}
          >
            {convertToTitleCase(contribution?.status || "SUCCESSFUL")}
          </span>
        </div>

        <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
          <p className="text-sm text-muted mb-0">Withdrawable Status</p>
          <span
            className="px-3 py-1 rounded-pill text-white fw-semibold"
            style={{
              backgroundColor: isWithdrawable ? "var(--color-secondary)" : "var(--color-primary)",
              fontSize: "12px",
            }}
          >
            {isWithdrawable ? "Withdrawable" : "Non-Withdrawable"}
          </span>
        </div>

        {contribution?.transactionCode && (
          <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
            <p className="text-sm text-muted mb-0">Transaction Code</p>
            <p className="text-sm fw-bold text-black1 mb-0">
              {contribution.transactionCode}
            </p>
          </div>
        )}

        {contribution?.reference && (
          <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
            <p className="text-sm text-muted mb-0">Reference</p>
            <p className="text-sm fw-bold text-black1 mb-0 font-monospace">
              {contribution.reference}
            </p>
          </div>
        )}

        {category?.amount && (
          <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
            <p className="text-sm text-muted mb-0">Maximum Amount</p>
            <p className="text-sm fw-bold text-black1 mb-0">
              {formatMoney(category.amount, true, "₦")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
